/**
 * AI search results page orchestrator (templates/page.ai-search.json).
 *
 * Flow for /pages/ki-suche?q=…:
 *   1. Fire /api/ai-search and /api/ai-summary in parallel — products never
 *      wait for the summary.
 *   2. Turn the AI hits into real theme cards the same way
 *      sections/main-brands.liquid does: fetch each product's full page with
 *      the alternate product.brand-card template (/products/<handle>?view=brand-card)
 *      and extract its fully-rendered <li class="product-grid__item"> card. The
 *      alternate template is the only place the complete card block tree exists
 *      (requesting the section in isolation renders an empty shell). Cards are
 *      appended in AI relevance order (the API returns them ordered, so the
 *      per-product fetches map 1:1). Benefits chips are appended per card from
 *      the hit's metafield_aico_metafields_benefits.
 *   3. Reveal banner + count from the AI total. The summary replaces the
 *      header title + intro; if it fails, title + intro are shown again.
 *   4. The summary names one or two best matches (?mentions=1: the API returns
 *      each named product with the exact words and their offset). Those words
 *      become links to the product, and the products move to the front of the
 *      grid — whichever of summary and grid arrives second does the move
 *      (task #15613, feedback C).
 *
 * Both API calls send ?fallback=1: a query nothing matches gets the closest
 * products plus a bridging text instead of an empty page (feedback A). The
 * app instance is shared with every other theme of the shop and keeps both
 * behaviours off unless a request asks — so they reach shoppers exactly when
 * this file does.
 */
(function () {
  'use strict';

  var FETCH_TIMEOUT_MS = 8000;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  // #143762: one dataLayer event per search a shopper completes, for the GA4
  // search terms; Google Tag Manager (GTM-WMC4VJV) sends it on to GA4.
  function pushSearchEvent(term, source) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'ki_search', search_term: term, search_source: source });
  }

  function fetchWithTimeout(url, options) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);
    var merged = Object.assign({}, options || {}, { signal: controller.signal });
    return fetch(url, merged).finally(function () { clearTimeout(timer); });
  }

  ready(function () {
    var root = document.querySelector('[data-ai-search-page]');
    if (!root) return;

    var cfg = {
      apiUrl: root.getAttribute('data-api-url') || '',
      shop: root.getAttribute('data-shop') || '',
      locale: root.getAttribute('data-locale') || 'de',
      searchUrl: root.getAttribute('data-search-url') || '/search',
      limit: parseInt(root.getAttribute('data-limit') || '12', 10)
    };
    var i18n = {
      loading: root.getAttribute('data-i18n-loading') || '',
      error: root.getAttribute('data-i18n-error') || 'AI search is unavailable.',
      errorLink: root.getAttribute('data-i18n-error-link') || '',
      noResults: root.getAttribute('data-i18n-no-results') || 'No matching products found.'
    };

    var grid = root.querySelector('ul.product-grid');
    var status = root.querySelector('[data-ai-status]');
    var sentinel = root.querySelector('[data-ai-sentinel]');
    var loadingEl = root.querySelector('[data-ai-loading]');
    var banner = document.querySelector('[data-ai-banner]');
    var summaryEl = document.querySelector('[data-ai-summary]');
    var maxChars = banner ? parseInt(banner.getAttribute('data-ai-max-chars') || '350', 10) : 350;

    var params = new URLSearchParams(window.location.search);
    var query = (params.get('q') || '').trim();

    // Handles of the products the summary names, in reading order; null until
    // the summary has answered.
    var mentionHandles = null;
    var firstPageRendered = false;

    // Prefill the assistant box with the current query.
    document.querySelectorAll('[data-ai-query-prefill]').forEach(function (input) {
      if (query) input.value = query;
    });

    function hideSkeletons() {
      root.querySelectorAll('[data-ai-skeleton]').forEach(function (el) { el.remove(); });
    }

    function showStatus(message, withNativeLink) {
      hideSkeletons();
      if (!status) return;
      status.hidden = false;
      status.textContent = '';
      var p = document.createElement('p');
      p.textContent = message;
      status.appendChild(p);
      if (withNativeLink && i18n.errorLink) {
        var link = document.createElement('a');
        link.href = cfg.searchUrl + '?q=' + encodeURIComponent(query);
        link.textContent = i18n.errorLink;
        status.appendChild(link);
      }
    }

    function updateCount(total) {
      document.querySelectorAll('[data-ai-count-value]').forEach(function (el) {
        el.textContent = total;
      });
      document.querySelectorAll('[data-ai-count]').forEach(function (el) {
        el.hidden = false;
      });
    }

    function removeSummarySkeleton() {
      document.querySelectorAll('[data-ai-summary-skeleton]').forEach(function (el) { el.remove(); });
    }

    // Without an answer the header would be empty — bring back title + intro.
    function summaryFailed() {
      removeSummarySkeleton();
      document.documentElement.classList.add('ai-search-summary-failed');
    }

    function revealBanner() {
      if (banner) banner.hidden = false;
    }

    if (!query) {
      // Idle visit without a query: keep the page quiet — header explains it.
      hideSkeletons();
      return;
    }

    // Once per page load: the scroll pages below are the same search. Enter in
    // the header or homepage search lands here too.
    pushSearchEvent(query, 'page');

    if (!cfg.apiUrl || !cfg.shop) {
      summaryFailed();
      showStatus(i18n.error, true);
      return;
    }

    /* ---------- summary (independent of the products path) ---------- */

    var summaryUrl = cfg.apiUrl + '/api/ai-summary' +
      '?q=' + encodeURIComponent(query) +
      '&shop=' + encodeURIComponent(cfg.shop) +
      '&locale=' + encodeURIComponent(cfg.locale) +
      '&maxChars=' + maxChars +
      '&mentions=1&fallback=1&understand=1';

    fetchWithTimeout(summaryUrl)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.success && data.summary && summaryEl) {
          var mentions = renderSummary(summaryEl, data.summary, data.mentions);
          summaryEl.hidden = false;
          removeSummarySkeleton();
          mentionHandles = mentions.map(function (m) { return m.handle; });
          if (firstPageRendered) applyMentionOrder();
        } else {
          summaryFailed();
        }
      })
      .catch(summaryFailed);

    // Build the answer from text nodes and links — never innerHTML, the text
    // comes from a language model. Each mention is used only if its phrase is
    // found verbatim at its offset and after the previous link; anything else
    // stays plain text. Returns the mentions that became links.
    function renderSummary(el, text, mentions) {
      el.textContent = '';
      var linked = [];
      var cursor = 0;
      var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      (Array.isArray(mentions) ? mentions : []).forEach(function (m) {
        if (!m || !m.handle || !m.phrase || typeof m.offset !== 'number') return;
        if (m.offset < cursor) return;
        if (text.substr(m.offset, m.phrase.length) !== m.phrase) return;

        if (m.offset > cursor) el.appendChild(document.createTextNode(text.slice(cursor, m.offset)));

        var link = document.createElement('a');
        link.className = 'ai-search-page__answer-link';
        link.href = routesRoot + 'products/' + encodeURIComponent(m.handle);
        link.textContent = m.phrase;
        el.appendChild(link);

        linked.push(m);
        cursor = m.offset + m.phrase.length;
      });

      if (cursor < text.length) el.appendChild(document.createTextNode(text.slice(cursor)));
      return linked;
    }

    // Move the named products to the front of the grid, first-named first.
    // Only cards already on the page move: the summary reads from the top of
    // the same ranking, so its picks are on page 1. Idempotent.
    function applyMentionOrder() {
      if (!grid || !mentionHandles || !mentionHandles.length) return;
      for (var i = mentionHandles.length - 1; i >= 0; i--) {
        var card = findCard(mentionHandles[i]);
        if (card && grid.firstElementChild !== card) grid.insertBefore(card, grid.firstElementChild);
      }
    }

    function findCard(handle) {
      var cards = grid.querySelectorAll('[data-ai-handle]');
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].getAttribute('data-ai-handle') === handle) return cards[i];
      }
      return null;
    }

    /* ---------- products (paged, infinite scroll) ---------- */

    // The /api/ai-search endpoint paginates with &page=N (1-based) and returns
    // a `pagination` object: { currentPage, totalPages, hasNextPage, total, … }.
    // We load one page at a time as the sentinel scrolls into view — the same
    // pattern as the brands grid (sections/main-brands.liquid).
    var nextPage = 1;
    var loading = false;
    var done = false;
    var loadedCount = 0;
    var observer = null;

    function pageApiUrl(page) {
      return cfg.apiUrl + '/api/ai-search' +
        '?q=' + encodeURIComponent(query) +
        '&shop=' + encodeURIComponent(cfg.shop) +
        '&locale=' + encodeURIComponent(cfg.locale) +
        '&limit=' + cfg.limit +
        '&page=' + page +
        '&fallback=1&understand=1';
    }

    function finish() {
      done = true;
      if (observer) observer.disconnect();
      if (loadingEl) loadingEl.hidden = true;
    }

    function loadNextPage() {
      if (loading || done) return;
      loading = true;
      if (loadingEl) loadingEl.hidden = false;

      // The Liquid template renders the first page's skeletons; for later pages
      // add a batch here. Either way the skeletons stay visible through BOTH the
      // /api/ai-search call AND the per-product ?view=brand-card card fetches,
      // and are only removed right before the real cards are appended — so the
      // grid keeps its shape until the very last page.
      var skeletons = nextPage > 1
        ? addSkeletons(cfg.limit)
        : Array.prototype.slice.call(root.querySelectorAll('[data-ai-skeleton]'));

      fetchWithTimeout(pageApiUrl(nextPage))
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];
          var pageMeta = (data && data.pagination) || {};
          var total = pageMeta.total != null ? pageMeta.total : (data && data.total);

          if (!products.length) {
            removeSkeletons(skeletons);
            if (nextPage === 1) {
              // No results at all.
              finish();
              hideSkeletons();
              showStatus(i18n.noResults, false);
              updateCount(0);
              revealBanner();
              return;
            }
            // No more pages.
            finish();
            loading = false;
            return;
          }

          if (nextPage === 1) {
            updateCount(total != null ? total : products.length);
            revealBanner();
          }

          // Keep relevance order; carry each hit's benefits with its handle.
          // Only hits with a handle can be rendered via ?view=brand-card.
          var ordered = products
            .map(function (p) {
              return {
                handle: p.handle,
                benefits: normalizeBenefits(p.metafield_aico_metafields_benefits)
              };
            })
            .filter(function (item) { return item.handle; });

          // Fetch this page's cards, THEN drop the skeletons and append — so the
          // skeletons remain on screen the whole time the cards are in flight.
          return renderPage(ordered).then(function (cards) {
            removeSkeletons(skeletons);
            if (status) status.hidden = true;
            appendCards(cards);
            if (nextPage === 1) {
              firstPageRendered = true;
              applyMentionOrder();
            }

            // Stop when the API reports no further pages.
            if (pageMeta.hasNextPage === false ||
                (pageMeta.totalPages != null && pageMeta.currentPage != null &&
                 pageMeta.currentPage >= pageMeta.totalPages)) {
              finish();
            }
            nextPage += 1;
            loading = false;
            if (loadingEl) loadingEl.hidden = true;
            // If the sentinel is still visible (short pages / tall viewport),
            // keep loading until it scrolls out or we run out of pages.
            maybeLoadMore();
          });
        })
        .catch(function () {
          removeSkeletons(skeletons);
          if (nextPage === 1) {
            hideSkeletons();
            showStatus(i18n.error, true);
          }
          // Leave what's already loaded in place on a later-page error.
          finish();
        });
    }

    // Fetch a page's cards in parallel (relevance order preserved). Returns the
    // resolved card list; the caller removes skeletons and appends them.
    function renderPage(ordered) {
      if (!ordered.length) return Promise.resolve([]);
      return Promise.all(
        ordered.map(function (item) {
          return fetchProductCard(item.handle).then(function (li) {
            return li ? { li: li, benefits: item.benefits, handle: item.handle } : null;
          });
        })
      ).then(function (results) {
        return results.filter(Boolean);
      });
    }

    // aico PIM benefits arrive as a newline-separated string (or a list).
    function normalizeBenefits(raw) {
      if (!raw) return [];
      var values = Array.isArray(raw) ? raw : String(raw).split(/\r?\n+/);
      return values.map(function (v) { return v.trim(); }).filter(Boolean).slice(0, 4);
    }

    // Fetch a single product card. The response is the full product.brand-card
    // page; sections/brand-product-card.liquid renders the card as
    // <li class="product-grid__item" data-brand-product-card>. Parse with
    // DOMParser and pull out just that <li>. Returns null on any failure (e.g. a
    // 404 for an unpublished product) so the caller can drop it.
    function fetchProductCard(handle) {
      var url = '/products/' + encodeURIComponent(handle) + '?view=brand-card';
      return fetchWithTimeout(url)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          return doc.querySelector('li.product-grid__item, [data-brand-product-card]');
        })
        .catch(function () {
          return null;
        });
    }

    function appendBenefits(li, benefits) {
      if (!benefits || !benefits.length) return;
      var wrap = document.createElement('div');
      wrap.className = 'ai-product-benefits';
      benefits.forEach(function (text) {
        var chip = document.createElement('span');
        chip.className = 'ai-product-benefits__chip';
        chip.textContent = text;
        wrap.appendChild(chip);
      });
      li.appendChild(wrap);
    }

    function appendCards(cards) {
      if (!grid || !cards.length) return;
      var fragment = document.createDocumentFragment();
      cards.forEach(function (card) {
        // Import into the live document so custom elements (product-card /
        // add-to-cart-component, registered globally) upgrade on append.
        var node = document.importNode(card.li, true);
        node.classList.add('product-grid__item--' + loadedCount);
        node.setAttribute('data-ai-handle', card.handle);
        appendBenefits(node, card.benefits);
        fragment.appendChild(node);
        loadedCount += 1;
      });
      grid.appendChild(fragment);
      document.dispatchEvent(new CustomEvent('ai-search:results-rendered'));
    }

    // ---- Per-page skeleton placeholders (later pages) ----
    function addSkeletons(count) {
      var added = [];
      if (!grid) return added;
      for (var i = 0; i < count; i++) {
        var li = document.createElement('li');
        li.className = 'product-grid__item ai-search-skeleton';
        li.setAttribute('data-ai-skeleton', '');
        li.setAttribute('aria-hidden', 'true');
        li.innerHTML =
          '<div class="ai-search-skeleton__gallery"></div>' +
          '<span class="ai-search-skeleton__bar ai-search-skeleton__bar--title"></span>' +
          '<span class="ai-search-skeleton__bar ai-search-skeleton__bar--text"></span>' +
          '<span class="ai-search-skeleton__bar ai-search-skeleton__bar--text ai-search-skeleton__bar--short"></span>' +
          '<span class="ai-search-skeleton__bar ai-search-skeleton__bar--price"></span>' +
          '<span class="ai-search-skeleton__bar ai-search-skeleton__bar--button"></span>';
        grid.appendChild(li);
        added.push(li);
      }
      return added;
    }

    function removeSkeletons(nodes) {
      nodes.forEach(function (node) {
        if (node && node.parentNode) node.parentNode.removeChild(node);
      });
    }

    // ---- Infinite-scroll trigger ----
    function maybeLoadMore() {
      if (done || loading || !sentinel) return;
      var rect = sentinel.getBoundingClientRect();
      var viewportH = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top <= viewportH + 400) loadNextPage();
    }

    function startObserver() {
      if (!sentinel || !('IntersectionObserver' in window)) {
        // Fallback: no observer support — load a single page.
        loadNextPage();
        return;
      }
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) loadNextPage();
        });
      }, { rootMargin: '400px 0px' });
      observer.observe(sentinel);
    }

    // Kick off: load page 1, then let the observer drive subsequent pages.
    loadNextPage();
    startObserver();
  });
})();
